import { useState, useEffect } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  Mail,
  Phone,
  MapPin,
  ImageIcon,
  FileText,
  AlertCircle,
} from "lucide-react"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { formatCurrency } from "@/utils/format"
import { format } from "date-fns"
import { toast } from "sonner"

export default function ReturnDetails() {
  const { returnId } = useParams()
  const navigate = useNavigate()
  const {
    getReturnRequestById,
    approveReturnRequest,
    rejectReturnRequest,
    processReturnRequest,
  } = useAdminAuth()

  const [returnRequest, setReturnRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [processDialogOpen, setProcessDialogOpen] = useState(false)
  const [adminNote, setAdminNote] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")

  // Fetch return request data
  useEffect(() => {
    const fetchReturnRequest = async () => {
      if (!returnId) {
        setError("Return ID is required")
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      try {
        const data = await getReturnRequestById(returnId)
        setReturnRequest(data.returnRequest || data)
      } catch (err) {
        setError(err.message || "Failed to load return request details")
      } finally {
        setLoading(false)
      }
    }

    fetchReturnRequest()
  }, [returnId, getReturnRequestById])

  const refreshReturnRequest = async () => {
    if (!returnId) return
    try {
      const data = await getReturnRequestById(returnId)
      setReturnRequest(data.returnRequest || data)
    } catch (err) {
      console.error("Error refreshing return request:", err)
    }
  }

  const handleApprove = async () => {
    if (!returnId) return
    setActionLoading(true)
    try {
      await approveReturnRequest(returnId, adminNote || null)
      setApproveDialogOpen(false)
      setAdminNote("")
      await refreshReturnRequest()
    } catch (err) {
      console.error("Error approving return:", err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!returnId || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason")
      return
    }
    setActionLoading(true)
    try {
      await rejectReturnRequest(returnId, rejectionReason.trim(), adminNote || null)
      setRejectDialogOpen(false)
      setRejectionReason("")
      setAdminNote("")
      await refreshReturnRequest()
    } catch (err) {
      console.error("Error rejecting return:", err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleProcess = async (action) => {
    if (!returnId) return
    setActionLoading(true)
    try {
      await processReturnRequest(returnId, action, adminNote || null, true)
      setProcessDialogOpen(false)
      setAdminNote("")
      await refreshReturnRequest()
    } catch (err) {
      console.error("Error processing return:", err)
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      Pending: { variant: "secondary", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
      Approved: { variant: "default", className: "bg-blue-100 text-blue-700 border-blue-300" },
      Rejected: { variant: "destructive", className: "bg-red-100 text-red-700 border-red-300" },
      Processing: { variant: "secondary", className: "bg-purple-100 text-purple-700 border-purple-300" },
      Completed: { variant: "default", className: "bg-green-100 text-green-700 border-green-300" },
    }
    return variants[status] || { variant: "secondary", className: "bg-gray-100 text-gray-700" }
  }

  const canApprove = returnRequest?.status === "Pending"
  const canReject = returnRequest?.status === "Pending"
  const canProcess = returnRequest?.status === "Approved"
  const canComplete = returnRequest?.status === "Approved" || returnRequest?.status === "Processing"

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    )
  }

  if (error || !returnRequest) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <Package className="w-16 h-16 text-gray-400" />
        <p className="text-gray-500">{error || "Return request not found."}</p>
        <Link to="/admin/returns">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Returns
          </Button>
        </Link>
      </div>
    )
  }

  const statusBadge = getStatusBadge(returnRequest.status)
  const order = returnRequest.order || {}
  const user = returnRequest.user || {}
  const items = returnRequest.items || []
  const images = returnRequest.images || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Package className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Return Request #{returnRequest.returnId}</h1>
            <p className="text-muted-foreground">Manage return request for Order #{returnRequest.orderId}</p>
          </div>
        </div>
        <Link to="/admin/returns">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Returns
          </Button>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Return Request Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Return Request Details</CardTitle>
                <Badge className={statusBadge.className}>{returnRequest.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Reason for Return</Label>
                <p className="mt-1">{returnRequest.reason || "—"}</p>
              </div>

              {returnRequest.adminNote && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Admin Note</Label>
                  <p className="mt-1 text-sm">{returnRequest.adminNote}</p>
                </div>
              )}

              <Separator />

              {/* Return Items */}
              <div>
                <Label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Items to Return ({items.length})
                </Label>
                <div className="space-y-3">
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{item.productName || item.name || "Product"}</p>
                          {item.sku && <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>}
                          <p className="text-sm text-muted-foreground">Quantity: {item.quantity || 1}</p>
                        </div>
                        {item.price && (
                          <div className="text-right">
                            <p className="font-medium">{formatCurrency(item.price)}</p>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No items specified</p>
                  )}
                </div>
              </div>

              {/* Return Images */}
              {images.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground mb-3 block">
                      Return Images ({images.length})
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {images.map((imageUrl, index) => (
                        <div key={index} className="relative aspect-square border rounded-lg overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={`Return image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "/placeholder.svg?height=200&width=200"
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Order Items */}
              {order.items && order.items.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground mb-3 block">
                      Original Order Items
                    </Label>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border rounded text-sm">
                          <div>
                            <p className="font-medium">{item.product_name || "Product"}</p>
                            {item.variant_name && (
                              <p className="text-xs text-muted-foreground">Variant: {item.variant_name}</p>
                            )}
                            <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-medium">{formatCurrency(item.final_price || 0)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Order Information */}
          <Card>
            <CardHeader>
              <CardTitle>Order Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order ID</Label>
                  <div className="mt-1">
                    <Link
                      to={`/admin/orders/${order.orderId || returnRequest.orderId}`}
                      className="text-primary hover:underline font-mono"
                    >
                      #{order.orderId || returnRequest.orderId}
                    </Link>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Status</Label>
                  <p className="mt-1">{order.status || "—"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Total</Label>
                  <p className="mt-1 font-medium">{formatCurrency(order.total || 0)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Order Date</Label>
                  <p className="mt-1">
                    {order.createdAt
                      ? format(new Date(order.createdAt), "MMM dd, yyyy 'at' h:mm a")
                      : "—"}
                  </p>
                </div>
              </div>

              {order.shippingAddress && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground mb-2 block">
                      Shipping Address
                    </Label>
                    <div className="text-sm space-y-1">
                      {typeof order.shippingAddress === "object" ? (
                        <>
                          {order.shippingAddress.street && <p>{order.shippingAddress.street}</p>}
                          {order.shippingAddress.city && order.shippingAddress.state && (
                            <p>
                              {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                              {order.shippingAddress.zipCode}
                            </p>
                          )}
                          {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                        </>
                      ) : (
                        <p>{order.shippingAddress}</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Actions & Info */}
        <div className="space-y-6">
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canApprove && (
                <Button
                  className="w-full"
                  onClick={() => setApproveDialogOpen(true)}
                  disabled={actionLoading}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Return
                </Button>
              )}

              {canReject && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setRejectDialogOpen(true)}
                  disabled={actionLoading}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Return
                </Button>
              )}

              {canProcess && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setProcessDialogOpen(true)}
                  disabled={actionLoading}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Mark as Processing
                </Button>
              )}

              {canComplete && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => handleProcess("completed")}
                  disabled={actionLoading || returnRequest.status === "Completed"}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}

              {returnRequest.status === "Completed" && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  Return completed
                </div>
              )}

              {returnRequest.status === "Rejected" && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <XCircle className="w-4 h-4" />
                  Return rejected
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Name
                </Label>
                <p className="mt-1">{user.name || "—"}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <p className="mt-1">{user.email || "—"}</p>
              </div>
              {user.phone && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone
                  </Label>
                  <p className="mt-1">{user.phone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Request Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Requested</Label>
                <p className="mt-1 text-sm">
                  {returnRequest.createdAt
                    ? format(new Date(returnRequest.createdAt), "MMM dd, yyyy 'at' h:mm a")
                    : "—"}
                </p>
              </div>
              {returnRequest.updatedAt && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last Updated</Label>
                  <p className="mt-1 text-sm">
                    {format(new Date(returnRequest.updatedAt), "MMM dd, yyyy 'at' h:mm a")}
                  </p>
                </div>
              )}
              {returnRequest.processedAt && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Processed</Label>
                  <p className="mt-1 text-sm">
                    {format(new Date(returnRequest.processedAt), "MMM dd, yyyy 'at' h:mm a")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Return Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this return request? The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="approve-note">Admin Note (Optional)</Label>
              <Textarea
                id="approve-note"
                placeholder="Add any internal notes about this approval..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={actionLoading}>
              {actionLoading ? "Approving..." : "Approve Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Return Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this return request. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                Rejection Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Explain why this return request is being rejected..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reject-note">Admin Note (Optional)</Label>
              <Textarea
                id="reject-note"
                placeholder="Add any internal notes..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading || !rejectionReason.trim()}>
              {actionLoading ? "Rejecting..." : "Reject Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Return Request</DialogTitle>
            <DialogDescription>
              Mark this return as processing. You can complete it later once the items are received.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="process-note">Admin Note (Optional)</Label>
              <Textarea
                id="process-note"
                placeholder="Add any notes about processing this return..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleProcess("processing")} disabled={actionLoading}>
              {actionLoading ? "Processing..." : "Mark as Processing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

